// Call tones (ringback for the caller, ringtone for the callee).
//
// Tones are synthesised with the Web Audio API so no audio asset ships with the
// app and nothing is ever recorded or uploaded. The scheduler restarts the
// pattern on an interval and tears everything down when the tone stops.
import { useEffect, useRef } from "react";

export type CallTone = "ringback" | "ringtone" | null;

type Blip = { at: number; dur: number; freqs: number[]; gain: number };

// Ringback = the low, calm "waiting" tone the caller hears (2s on, 4s off).
const RINGBACK: { period: number; blips: Blip[] } = {
  period: 6,
  blips: [{ at: 0, dur: 2, freqs: [440, 480], gain: 0.06 }],
};

// Ringtone = the brighter double-ring the receiver hears (siren-like sweep).
const RINGTONE: { period: number; blips: Blip[] } = {
  period: 3,
  blips: [
    { at: 0, dur: 0.4, freqs: [660, 880], gain: 0.1 },
    { at: 0.5, dur: 0.4, freqs: [880, 1120], gain: 0.1 },
    { at: 1.1, dur: 0.4, freqs: [660, 880], gain: 0.1 },
    { at: 1.6, dur: 0.4, freqs: [880, 1120], gain: 0.1 },
  ],
};

export function useCallTone(tone: CallTone) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nodesRef = useRef<Array<{ osc: OscillatorNode; gain: GainNode }>>([]);

  useEffect(() => {
    if (!tone || typeof window === "undefined") return;
    const Ctor = window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    let cancelled = false;
    const spec = tone === "ringback" ? RINGBACK : RINGTONE;
    const ctx = new Ctor();
    ctxRef.current = ctx;

    const playCycle = () => {
      if (cancelled || ctx.state === "closed") return;
      const start = ctx.currentTime + 0.02;
      for (const blip of spec.blips) {
        for (const freq of blip.freqs) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          const t0 = start + blip.at;
          const t1 = t0 + blip.dur;
          gain.gain.setValueAtTime(0, t0);
          gain.gain.linearRampToValueAtTime(blip.gain, t0 + 0.03);
          gain.gain.setValueAtTime(blip.gain, t1 - 0.05);
          gain.gain.linearRampToValueAtTime(0, t1);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t0);
          osc.stop(t1 + 0.02);
          const entry = { osc, gain };
          nodesRef.current.push(entry);
          osc.onended = () => {
            try {
              gain.disconnect();
              osc.disconnect();
            } catch {
              /* already torn down */
            }
            nodesRef.current = nodesRef.current.filter((n) => n !== entry);
          };
        }
      }
    };

    // Autoplay may be blocked until a gesture; the resume promise is best-effort.
    void ctx.resume().catch(() => undefined);
    playCycle();
    timerRef.current = setInterval(playCycle, spec.period * 1000);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      for (const { osc, gain } of nodesRef.current) {
        try {
          osc.stop();
          gain.disconnect();
          osc.disconnect();
        } catch {
          /* already stopped */
        }
      }
      nodesRef.current = [];
      ctxRef.current = null;
      void ctx.close().catch(() => undefined);
    };
  }, [tone]);
}
